/**
 * Invoice / quotation editor (design §C), full page, for /invoices/new
 * (`?clientId=`, `?docType=QUOTATION`) and /invoices/:docId/edit.
 *
 * Drafts save in place; an issued NON-tax invoice with no money or claims is
 * saved by the server as a revision (same number and link). Anything else is
 * locked and points back to the detail page.
 *
 * The form holds text exactly as typed (editorModel); the live preview runs
 * invoiceMath with the shop's tax rates. Once saved and unchanged, the
 * preview and totals show the server's document instead — its figures are
 * the real ones. Issue runs the server's checks first (validateForIssue) so
 * problems land on the fields, and server field errors are mapped back too.
 *
 * "Issue and send" opens the message composer in pre-issue mode (texts
 * rendered locally, number and link as placeholders), then saves and issues
 * with the send request; the invoice is issued even if the send fails.
 *
 * Quotations ask for a deposit on acceptance (whole percent) and are valid
 * for N days. An invoice converted from a quotation shows where it came from
 * and the quotation's paid deposit, which the server applies on issue.
 *
 * Not in this phase: "Preview as customer", attachments (no upload route yet).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import InvoiceRepository, { InvoiceDoc, InvoiceSettings, SendRequest } from '../../../internals/repository/InvoiceRepository';
import { InvoiceApiError } from '../../../internals/repository/authedPosApi';
import ClientRepository from '../../../internals/repository/ClientRepository';
import InvoicePreview from '../components/InvoicePreview';
import CustomerPicker, { ClientRecord, customerFromClient } from '../components/CustomerPicker';
import LineItemsTable from '../components/LineItemsTable';
import TotalsPanel from '../components/TotalsPanel';
import MessageComposer, { ComposerDialog, flashToast, TextSource, useCredits } from '../components/MessageComposer';
import {
  Channel, fillTemplate, Lang, measurable, numberPlaceholder, numberSample, preIssueVariables, sentToast, templateFor,
  unfillable,
} from '../composerModel';
import { addDays, date, daysBetween, money, moneyInput, TERMS, termDays } from '../format';
import { depositAmount, isQuoteId, originText } from '../quoteModel';
import { useInvoiceSettings, useSelectedShop } from '../useInvoicing';
import {
  blankState, chargeField, dueDateOf, EditorCustomer, EditorPreview, EditorState, editMode, fromDocument, lineTaxDefault,
  mapServerFields, newKey, parseDepositPct, previewDoc, Problems, serviceChargeMinor, toDocumentInput, validateForIssue,
  validateForSave, withSavedLineIds,
} from '../editorModel';

type Mode = 'draft' | 'revision' | 'locked';

const CODE_TEXT: Record<string, string> = {
  not_draft: 'This document is already issued and can no longer be edited here.',
  tax_invoice_locked: 'A tax invoice cannot be changed after issue. Issue a credit note and a new invoice.',
  revision_locked: 'This invoice has payments, a claim or a credit note and can no longer be changed.',
  forbidden_role: 'Ask the shop owner or an admin to do this.',
  invalid_phone: 'The customer’s phone number is not valid.',
  invalid_email: 'The customer’s email address is not valid.',
  tax_fields_required: 'A tax invoice needs these details',
  validation_failed: 'Fix these before saving',
};

const labelCls = 'flex flex-col gap-1.5 text-[13px] font-semibold text-text2';
const cardCls = 'rounded-2xl border bg-surface px-5 py-[18px]';
const errText = 'text-xs font-semibold text-[color:var(--negFg)]';
const bad = (on: unknown) => (on ? '!border-neg' : '');

const snapshot = (s: EditorState) => JSON.stringify(toDocumentInput(s));

function lockedReason(doc: InvoiceDoc): string {
  if (doc.status === 'VOID') return 'This document is void and cannot be edited.';
  if (doc.docType === 'QUOTATION') return 'Only draft quotations can be edited.';
  if (doc.docType !== 'INVOICE') return 'Credit notes and receipts cannot be edited.';
  if (doc.isTaxInvoice) return CODE_TEXT.tax_invoice_locked;
  return CODE_TEXT.revision_locked;
}

function Switch({ on, onClick, label, disabled }: { on: boolean; onClick: () => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={onClick}
      className="flex items-center gap-2.5 rounded-md text-sm font-semibold text-text1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${on ? 'bg-brand' : 'bg-borderc2'}`}>
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${on ? 'left-[18px]' : 'left-0.5'}`} />
      </span>
      {label}
    </button>
  );
}

function AmountWithType({
  value, type, onValue, onType, label, invalid,
}: {
  value: string; type: 'AMOUNT' | 'PERCENT'; onValue: (v: string) => void; onType: (t: 'AMOUNT' | 'PERCENT') => void; label: string; invalid?: boolean;
}) {
  return (
    <span className={`flex h-10 overflow-hidden rounded-[10px] border bg-surface focus-within:border-brand focus-within:shadow-[0_0_0_3px_var(--brandSoft)] ${invalid ? 'border-neg' : 'border-borderc2'}`}>
      <input
        aria-label={label}
        aria-invalid={invalid || undefined}
        inputMode="decimal"
        placeholder="0"
        className="w-full min-w-0 bg-transparent px-3 text-sm font-normal tabular-nums text-text1 outline-none"
        value={value}
        onChange={(e) => onValue(e.target.value)}
      />
      <select
        aria-label={`${label} type`}
        className="border-l border-borderc bg-surface2 px-2 text-[13px] font-normal text-text1 outline-none"
        value={type}
        onChange={(e) => onType(e.target.value as 'AMOUNT' | 'PERCENT')}
      >
        <option value="AMOUNT">Rs</option>
        <option value="PERCENT">%</option>
      </select>
    </span>
  );
}

/**
 * "Valid for N days", kept in step with Valid until: typing days moves the
 * date, picking a date updates the days. Holds its own text so the field can
 * be cleared while typing.
 */
function ValidForInput({ issueDate, validUntil, onValidUntil, invalid }: {
  issueDate: string; validUntil: string; onValidUntil: (iso: string) => void; invalid?: boolean;
}) {
  const days = validUntil ? String(daysBetween(issueDate, validUntil)) : '';
  const [text, setText] = useState(days);
  useEffect(() => {
    setText((t) => (t.trim() !== '' && Number(t) === Number(days) ? t : days));
  }, [days]);
  return (
    <span className={`flex h-10 items-center overflow-hidden rounded-[10px] border bg-surface focus-within:border-brand focus-within:shadow-[0_0_0_3px_var(--brandSoft)] ${invalid ? 'border-neg' : 'border-borderc2'}`}>
      <input
        type="number"
        min={0}
        max={365}
        step={1}
        aria-label="Valid for (days)"
        className="w-full min-w-0 bg-transparent px-3 text-sm font-normal tabular-nums text-text1 outline-none"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          const n = Number(e.target.value);
          if (e.target.value.trim() !== '' && Number.isInteger(n) && n >= 0 && n <= 365) onValidUntil(addDays(issueDate, n));
        }}
      />
      <span className="pr-3 text-[13px] font-normal text-text3">days</span>
    </span>
  );
}

function Skeleton() {
  const bar = 'animate-pulse rounded-lg bg-[color:var(--neutralSoft)]';
  return (
    <div className="h-screen flex flex-col w-full p-6 overflow-auto [&>*]:shrink-0" aria-busy="true">
      <div className={`${bar} mb-2 h-4 w-28`} />
      <div className={`${bar} mb-6 h-8 w-56`} />
      <div className="grid grid-cols-1 gap-5 min-[1100px]:grid-cols-[minmax(0,1.15fr)_minmax(360px,0.85fr)]">
        <div className="flex flex-col gap-4">
          {[120, 180, 240, 140].map((h) => <div key={h} className={`${bar} rounded-2xl`} style={{ height: h }} />)}
        </div>
        <div className={`${bar} h-[600px] rounded-2xl`} />
      </div>
    </div>
  );
}

/**
 * "Issue and send" before the document exists as issued: the same rows,
 * template, language, text and cost as the share dialog, with texts from
 * settings.templates / templateDefaults and the editor's preview figures.
 */
function IssueSendDialog({
  shopId, settings, shop, state, preview, onClose, submit,
}: {
  shopId: string;
  settings: InvoiceSettings;
  shop: any;
  state: EditorState;
  preview: EditorPreview;
  onClose: () => void;
  submit: (req: SendRequest, key: string) => Promise<void>;
}) {
  const credits = useCredits(shopId);
  const [busy, setBusy] = useState(false);
  const isQuote = state.docType === 'QUOTATION';
  const series = (settings as any).series || {};
  const fmt = (isQuote ? series.QUOTATION : state.isTaxInvoice && series.TAX_INVOICE ? series.TAX_INVOICE : series.INVOICE)?.format;
  const year = (state.issueDate || '').slice(0, 4) || String(new Date().getFullYear());
  const number = numberPlaceholder(fmt, year);
  const sample = numberSample(fmt, year);
  const startLang = ((settings as any).defaultLanguage as Lang) || 'en';

  const varsFor = useCallback((lang: Lang) => preIssueVariables({
    docType: state.docType,
    customerName: state.customer?.name || '',
    shopName: shop?.shopName || '',
    shopPhone: shop?.phoneNumber || '',
    number,
    totalMinor: preview.totalMinor ?? 0,
    balanceMinor: preview.balanceMinor ?? preview.totalMinor ?? 0,
    dueDate: preview.dueDate,
    validUntil: preview.validUntil,
    lang,
  }), [state.docType, state.customer, shop, number, preview]);

  const source = useCallback<TextSource>(async ({ channel, language, templateKey }) => {
    const lang = language || startLang;
    const key = templateKey || (isQuote ? 'quotation_sent' : 'invoice_issued');
    const t = templateFor(settings as any, key, channel, lang);
    const vars = varsFor(lang);
    return {
      text: fillTemplate(t.text, vars),
      subject: t.subject ? fillTemplate(t.subject, vars) : undefined,
      templateKey: key,
      language: lang,
    };
  }, [settings, varsFor, startLang, isQuote]);

  const unfillVars = useCallback((lang: Lang) => unfillable(varsFor(lang)), [varsFor]);
  const measure = useCallback((t: string) => measurable(t, { number, numberSample: sample }), [number, sample]);

  return (
    <ComposerDialog
      title="Issue and send"
      sub={`${state.customer?.companyName || state.customer?.name || 'Customer'} · ${isQuote ? 'The quotation' : 'The invoice'} gets its number and link on issue, then Netlise sends it.`}
      onClose={onClose}
      busy={busy}
    >
      <MessageComposer
        mode="pre-issue"
        source={source}
        customer={{ phone: state.customer?.phone, email: state.customer?.email }}
        credits={credits}
        defaultChannels={(settings as any).defaultChannels as Channel[] | undefined}
        unfillVars={unfillVars}
        measure={measure}
        submit={submit}
        onSent={() => undefined}
        onCancel={onClose}
        onBusyChange={setBusy}
      />
    </ComposerDialog>
  );
}

function Editor({ onCreated }: { onCreated: (docId: string) => void }) {
  const { docId: routeDocId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const shop = useSelectedShop();
  const shopId: string | undefined = shop?.shopId;
  const { settings, error: settingsError, reload: reloadSettings } = useInvoiceSettings(shopId);

  const [state, setState] = useState<EditorState | null>(null);
  const [docId, setDocId] = useState<string | undefined>(routeDocId);
  const [serverDoc, setServerDoc] = useState<InvoiceDoc | null>(null);
  const [mode, setMode] = useState<Mode | null>(routeDocId ? null : 'draft');
  const [baseline, setBaseline] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [attempted, setAttempted] = useState<'save' | 'issue' | null>(null);
  const [server, setServer] = useState<(Problems & { title: string }) | null>(null);
  const [busy, setBusy] = useState<'save' | 'issue' | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [sendOpen, setSendOpen] = useState(false);
  /** The quotation an invoice draft was converted from (parentDocId quo_…). */
  const [parentQuote, setParentQuote] = useState<InvoiceDoc | null>(null);
  const [zoom, setZoom] = useState(() => (typeof window !== 'undefined' && window.innerWidth < 1300 ? 60 : 70));
  /** One key per editor session, so a double click cannot create two drafts. */
  const idemKey = useRef(newKey('idem'));
  /** The id this editor just created; its URL change must not reload it. */
  const createdId = useRef<string | null>(null);
  const topRef = useRef<HTMLDivElement>(null);

  // ── Load ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (routeDocId || !settings || state) return;
    const docType = params.get('docType') === 'QUOTATION' ? 'QUOTATION' : 'INVOICE';
    const s = blankState(settings, docType);
    const clientId = params.get('clientId');
    if (clientId) {
      s.customer = { source: 'client', clientId, name: '', companyName: '', phone: '', email: '', address: '', tin: '', vatRegistered: false };
    }
    setState(s);
    setBaseline(snapshot(s));
  }, [routeDocId, settings, state, params]);

  // Resolve a preselected ?clientId= to its name for the card and preview.
  useEffect(() => {
    const id = params.get('clientId');
    if (routeDocId || !shopId || !id) return;
    let live = true;
    (ClientRepository.fetchClients(shopId, 1000, null) as Promise<any>).then((res) => {
      const found = (res?.items ?? []).find((c: ClientRecord) => c.id === id);
      if (!live || !found) return;
      setState((s) => (s && s.customer?.clientId === id && !s.customer.name ? { ...s, customer: customerFromClient(found) } : s));
    }).catch(() => undefined);
    return () => { live = false; };
  }, [routeDocId, shopId, params]);

  useEffect(() => {
    setDocId(routeDocId);
    if (!routeDocId || !shopId || routeDocId === createdId.current) return;
    let live = true;
    setLoadError(null);
    InvoiceRepository.get(shopId, routeDocId).then(({ document }) => {
      if (!live) return;
      const m = editMode(document);
      setServerDoc(document);
      setMode(m);
      if (m !== 'locked') {
        const s = fromDocument(document);
        setState(s);
        setBaseline(snapshot(s));
      }
    }).catch((e) => {
      if (live) setLoadError(e instanceof InvoiceApiError && e.code === 'not_found' ? 'This document does not exist.' : e?.message || 'The document could not load.');
    });
    return () => { live = false; };
  }, [routeDocId, shopId]);

  // An invoice converted from a quotation: show its origin and the deposit
  // paid on it, which the server applies when this invoice is issued.
  const parentId = serverDoc?.docType === 'INVOICE' && isQuoteId(serverDoc.parentDocId) ? serverDoc.parentDocId : undefined;
  useEffect(() => {
    if (!parentId || !shopId) { setParentQuote(null); return undefined; }
    let live = true;
    InvoiceRepository.get(shopId, parentId).then(({ document }) => {
      if (!live) return;
      setParentQuote(document);
      // Not part of the saved input (fromQuote), so this does not mark the form dirty.
      setState((s) => (s && s.fromQuote ? { ...s, deposit: document.paidMinor ? moneyInput(document.paidMinor) : '' } : s));
    }).catch(() => undefined);
    return () => { live = false; };
  }, [parentId, shopId]);

  // ── Derived ─────────────────────────────────────────────────────────────
  const current = useMemo(() => (state ? snapshot(state) : ''), [state]);
  const dirty = !!state && current !== baseline;

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const preview = useMemo(() => (state ? previewDoc(state, settings, shop) : null), [state, settings, shop]);

  const live = useMemo<Problems | null>(() => {
    if (!state || !attempted) return null;
    return attempted === 'issue' ? validateForIssue(state, settings) : validateForSave(state);
  }, [state, settings, attempted]);

  const fields = { ...(server?.fields ?? {}), ...(live?.fields ?? {}) };
  const bannerItems = Array.from(new Set([
    ...(live?.banner ?? []),
    // The live title wins, so a server message must still show as an item.
    ...(live?.banner.length && server?.title ? [server.title] : []),
    ...(server?.banner ?? []),
  ]));
  const bannerTitle = live?.banner.length
    ? `Fix ${live.banner.length} ${live.banner.length === 1 ? 'thing' : 'things'} before ${attempted === 'issue' ? 'issuing' : 'saving'}`
    : server?.title ?? '';

  // ── Updates ─────────────────────────────────────────────────────────────
  const patch = useCallback((p: Partial<EditorState>) => setState((s) => (s ? { ...s, ...p } : s)), []);

  const isQuote = state?.docType === 'QUOTATION';
  const taxAllowed = !!settings?.taxInvoiceEnabled && settings?.taxInvoiceMode !== 'NEVER';

  const setCustomer = (c: EditorCustomer | null) => setState((s) => {
    if (!s) return s;
    const next = { ...s, customer: c };
    if (c?.vatRegistered && s.docType === 'INVOICE' && mode === 'draft' && taxAllowed) next.isTaxInvoice = true;
    return next;
  });

  const setDocType = (docType: 'INVOICE' | 'QUOTATION') => setState((s) => (s ? {
    ...s, docType, isTaxInvoice: docType === 'QUOTATION' ? false : s.isTaxInvoice,
    validUntil: s.validUntil || blankState(settings, docType, s.issueDate).validUntil,
  } : s));

  // ── Save / issue ────────────────────────────────────────────────────────
  const fail = (e: unknown, s: EditorState) => {
    if (e instanceof InvoiceApiError) {
      const mapped = mapServerFields(e.fields, s);
      const title = e.status === 403 ? CODE_TEXT.forbidden_role : CODE_TEXT[e.code] || e.message;
      const extra: Record<string, string> = {};
      if (e.code === 'invalid_phone' || e.code === 'invalid_email') extra.customer = CODE_TEXT[e.code];
      setServer({
        title,
        banner: mapped.banner.length ? mapped.banner : (e.code === 'tax_fields_required' || e.code === 'validation_failed' ? [e.message] : []),
        fields: { ...mapped.fields, ...extra },
      });
    } else {
      setServer({ title: (e as any)?.message || 'Something went wrong. Try again.', banner: [], fields: {} });
    }
    topRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  /** Create or update; returns the saved document. */
  const persist = async (s: EditorState): Promise<InvoiceDoc> => {
    const input = toDocumentInput(s);
    let doc: InvoiceDoc;
    if (!docId) {
      doc = (await InvoiceRepository.create(shopId!, input, idemKey.current)).document;
      createdId.current = doc.docId;
      onCreated(doc.docId);
      setDocId(doc.docId);
      navigate(`/invoices/${encodeURIComponent(doc.docId)}/edit`, { replace: true });
    } else {
      const { docType, ...rest } = input;
      if (mode === 'revision') {
        delete rest.isTaxInvoice;
        delete rest.depositAppliedMinor;
        if (s.revisionNote.trim()) rest.revisionNote = s.revisionNote.trim();
      }
      doc = (await InvoiceRepository.update(shopId!, docId, rest)).document;
    }
    const saved = withSavedLineIds(s, doc);
    setState((cur) => (cur ? withSavedLineIds(cur, doc) : cur));
    setBaseline(snapshot(saved));
    setServerDoc(doc);
    return doc;
  };

  const saveDraft = async () => {
    if (!state || busy || !shopId) return;
    setServer(null);
    const p = validateForSave(state);
    if (p.banner.length) {
      setAttempted('save');
      topRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      return;
    }
    setBusy('save');
    try {
      const doc = await persist(state);
      if (mode === 'revision') {
        navigate(`/invoices/${encodeURIComponent(doc.docId)}`);
        return;
      }
      setAttempted(null);
      setToast('Draft saved');
    } catch (e) {
      fail(e, state);
    } finally {
      setBusy(null);
    }
  };

  const issue = async () => {
    if (!state || busy || !shopId) return;
    setServer(null);
    const p = validateForIssue(state, settings);
    if (p.banner.length) {
      setAttempted('issue');
      topRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      return;
    }
    setBusy('issue');
    try {
      const doc = await persist(state);
      await InvoiceRepository.issue(shopId, doc.docId);
      navigate(`/invoices/${encodeURIComponent(doc.docId)}`);
    } catch (e) {
      setAttempted('issue');
      fail(e, state);
    } finally {
      setBusy(null);
    }
  };

  /** Runs the issue checks, then opens the composer. */
  const openIssueSend = () => {
    if (!state || busy || !shopId) return;
    setServer(null);
    const p = validateForIssue(state, settings);
    if (p.banner.length) {
      setAttempted('issue');
      topRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      return;
    }
    setSendOpen(true);
  };

  /**
   * Save, then issue with the send request. Network and server trouble goes
   * back to the composer ("Try again", same key); anything else is a problem
   * with the document, so the dialog closes and the fields show it.
   */
  const issueAndSend = async (req: SendRequest, key: string) => {
    if (!state || !shopId) return;
    setBusy('issue');
    try {
      const doc = await persist(state);
      const res = await InvoiceRepository.issue(shopId, doc.docId, req, key);
      const sent = res.send;
      if (sent?.error) flashToast(`Issued. The message could not be sent: ${sent.error.message}`, 'warn');
      else flashToast(sent ? sentToast(sent, req.channels, 'Issued and sent by') : 'Issued');
      setBaseline(snapshot(state));
      navigate(`/invoices/${encodeURIComponent(doc.docId)}`);
    } catch (e: any) {
      const status = typeof e?.status === 'number' ? e.status : 0;
      if (status === 0 || status >= 500) throw e;
      setSendOpen(false);
      setAttempted('issue');
      fail(e, state);
    } finally {
      setBusy(null);
    }
  };

  const back = () => {
    if (dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
    if (docId && mode === 'revision') navigate(`/invoices/${encodeURIComponent(docId)}`);
    else navigate(state?.docType === 'QUOTATION' ? '/invoices/quotations' : '/invoices');
  };

  // ── Render: states ──────────────────────────────────────────────────────
  if (loadError) {
    return (
      <div className="h-screen flex flex-col w-full p-6 overflow-auto [&>*]:shrink-0">
        <Link to="/invoices" className="text-[13px] text-text3 hover:text-brand">← All invoices</Link>
        <div className={`${cardCls} mt-4 border-borderc`}>
          <p className="font-semibold text-text1">The document could not load</p>
          <p className="mt-1 text-sm text-text2">{loadError}</p>
        </div>
      </div>
    );
  }

  if (settingsError && !settings) {
    return (
      <div className="h-screen flex flex-col w-full p-6 overflow-auto [&>*]:shrink-0">
        <Link to="/invoices" className="text-[13px] text-text3 hover:text-brand">← All invoices</Link>
        <div className={`${cardCls} mt-4 flex items-center justify-between gap-4 border-borderc`}>
          <div>
            <p className="font-semibold text-text1">Invoice settings could not load</p>
            <p className="mt-1 text-sm text-text2">{settingsError.message}</p>
          </div>
          <button type="button" className="nl-btn nl-btn--secondary" onClick={reloadSettings}>Try again</button>
        </div>
      </div>
    );
  }

  if (mode === 'locked' && serverDoc) {
    return (
      <div className="h-screen flex flex-col w-full p-6 overflow-auto [&>*]:shrink-0">
        <Link to="/invoices" className="text-[13px] text-text3 hover:text-brand">← All invoices</Link>
        <h1 className="mt-1.5 text-[28px] font-semibold tracking-[-0.015em] text-text1">
          {serverDoc.number ? `Edit ${serverDoc.number}` : 'Edit document'}
        </h1>
        <div role="alert" className="mt-5 flex gap-3 rounded-xl border border-warn bg-warn-soft px-4 py-3.5">
          <span className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full bg-warn text-[13px] font-bold text-white">!</span>
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-text1">This document can no longer be edited</span>
            <span className="text-sm text-text2">{lockedReason(serverDoc)}</span>
            <Link to={`/invoices/${encodeURIComponent(serverDoc.docId)}`} className="mt-1 text-sm font-semibold text-brand hover:underline">
              Open {serverDoc.number || 'the document'} →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!state || !settings || !preview || !mode) return <Skeleton />;

  // ── Render: editor ──────────────────────────────────────────────────────
  const isRevision = mode === 'revision';
  const showTax = !isQuote && !isRevision && (taxAllowed || state.isTaxInvoice);
  const isTax = !isQuote && state.isTaxInvoice;
  const title = !docId
    ? (isQuote ? 'New quotation' : 'New invoice')
    : serverDoc?.number
      ? `Edit ${serverDoc.number}`
      : `Edit ${isQuote ? 'quotation' : 'invoice'} draft`;
  const days = termDays(state.paymentTerms, Number(state.customTermDays) || 0);
  const due = dueDateOf(state);
  const shown = !dirty && serverDoc ? serverDoc : preview;
  const newLineTax = lineTaxDefault(settings, isTax);
  const errCount = live?.banner.length ?? 0;
  const hint = errCount
    ? `${errCount} to fix before ${attempted === 'issue' ? 'issuing' : 'saving'}`
    : isRevision
      ? `Saving creates revision ${(serverDoc?.revision ?? 1) + 1}, with the same number and link.`
      : isQuote
        ? 'Issuing gives the quotation its number and marks it as Sent.'
        : 'Issuing gives the invoice its number and locks the lines.';

  const addCharge = (label: string, amount: string) =>
    patch({ charges: [...state.charges, { key: newKey('c'), label, amount }] });

  return (
    <div className="h-screen flex flex-col w-full p-6 overflow-auto [&>*]:shrink-0">
      <div ref={topRef} className="mx-auto flex w-full max-w-[1440px] flex-col gap-[18px]">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-col gap-1.5">
            <button type="button" onClick={back} className="self-start rounded text-[13px] text-text3 hover:text-brand focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand">
              ← {isRevision ? serverDoc?.number || 'Invoice' : isQuote ? 'Quotations' : 'All invoices'}
            </button>
            <h1 className="m-0 text-[28px] font-semibold tracking-[-0.015em] text-text1">{title}</h1>
            {isRevision && (
              <span className="text-[13px] text-text2">Issued, not yet paid. Changes are saved as a new revision.</span>
            )}
          </div>
          {!docId && (
            <div role="radiogroup" aria-label="Document type" className="flex gap-0.5 rounded-[10px] border border-borderc bg-surface2 p-[3px]">
              {(['INVOICE', 'QUOTATION'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  role="radio"
                  aria-checked={state.docType === t}
                  onClick={() => setDocType(t)}
                  className={`h-8 rounded-lg px-3.5 text-[13px] font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand ${
                    state.docType === t ? 'bg-surface text-text1 shadow-sm' : 'text-text2'
                  }`}
                >
                  {t === 'INVOICE' ? 'Invoice' : 'Quotation'}
                </button>
              ))}
            </div>
          )}
        </div>

        {state.fromQuote && parentId && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-brand bg-brand-soft px-4 py-3 text-sm">
            <span className="font-semibold text-brand">
              {parentQuote ? originText(parentQuote, parentQuote.currency) : 'From a quotation'}
            </span>
            <span className="text-text2">Issuing this invoice marks the quotation as Invoiced.</span>
            <Link to={`/invoices/${encodeURIComponent(parentId)}`} className="ml-auto font-semibold text-brand hover:underline">
              Open {parentQuote?.number || 'quotation'}
            </Link>
          </div>
        )}

        {(bannerTitle || bannerItems.length > 0) && (
          <div role="alert" className="flex gap-3 rounded-xl border border-neg bg-neg-soft px-4 py-3.5">
            <span className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full bg-neg text-[13px] font-bold text-white">!</span>
            <div className="flex flex-col gap-0.5 text-sm">
              <span className="font-semibold text-text1">{bannerTitle || 'Fix these before continuing'}</span>
              {bannerItems.map((b) => <span key={b} className="text-text2">· {b}</span>)}
              {fields.shopTin && (
                <Link to="/settings/invoicing" className="mt-1 font-semibold text-brand hover:underline">Open Settings → Invoicing</Link>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 items-start gap-5 min-[1100px]:grid-cols-[minmax(0,1.15fr)_minmax(360px,0.85fr)]">
          {/* Left column */}
          <div className="flex min-w-0 flex-col gap-4">
            {/* Customer */}
            <section aria-label="Customer" className={`${cardCls} flex flex-col gap-3 ${fields.customer || fields['customer.name'] ? 'border-neg' : 'border-borderc'}`}>
              <h2 className="text-base font-semibold text-text1">Customer</h2>
              <CustomerPicker
                shopId={shopId}
                value={state.customer}
                onChange={setCustomer}
                error={!state.customer ? fields.customer : undefined}
              />
              {state.customer && (fields.customer || fields['customer.name']) && (
                <span className={errText}>! {fields.customer || fields['customer.name']}</span>
              )}
            </section>

            {/* Details */}
            <section aria-label="Details" className={`${cardCls} grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-x-4 gap-y-3.5 border-borderc`}>
              <div className="col-span-full flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-base font-semibold text-text1">Details</h2>
                {showTax && (
                  <Switch
                    on={state.isTaxInvoice}
                    label="Tax invoice"
                    onClick={() => patch({ isTaxInvoice: !state.isTaxInvoice })}
                  />
                )}
              </div>
              <label className={labelCls}>
                Issue date
                <input
                  type="date"
                  className={`nl-input font-normal ${bad(fields.issueDate)}`}
                  value={state.issueDate}
                  onChange={(e) => {
                    const issueDate = e.target.value || state.issueDate;
                    // A quotation keeps its "valid for" days when the issue date moves.
                    if (isQuote && state.validUntil) {
                      patch({ issueDate, validUntil: addDays(issueDate, daysBetween(state.issueDate, state.validUntil)) });
                    } else patch({ issueDate });
                  }}
                />
                {fields.issueDate && <span className={errText}>! {fields.issueDate}</span>}
              </label>
              {isQuote ? (
                <>
                  <div className={labelCls}>
                    <span>Valid for</span>
                    <ValidForInput
                      issueDate={state.issueDate}
                      validUntil={state.validUntil}
                      invalid={!!fields.validUntil}
                      onValidUntil={(validUntil) => patch({ validUntil })}
                    />
                  </div>
                  <label className={labelCls}>
                    Valid until
                    <input
                      type="date"
                      className={`nl-input font-normal ${bad(fields.validUntil)}`}
                      value={state.validUntil}
                      min={state.issueDate}
                      onChange={(e) => patch({ validUntil: e.target.value })}
                    />
                    {fields.validUntil && <span className={errText}>! {fields.validUntil}</span>}
                  </label>
                  <label className={labelCls}>
                    Deposit on accept (%)
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      inputMode="numeric"
                      placeholder="0"
                      className={`nl-input font-normal tabular-nums ${bad(fields.depositPct)}`}
                      aria-invalid={!!fields.depositPct || undefined}
                      value={state.depositPct}
                      onChange={(e) => patch({ depositPct: e.target.value })}
                    />
                    {fields.depositPct
                      ? <span className={errText}>! {fields.depositPct}</span>
                      : parseDepositPct(state.depositPct) > 0 && (
                        <span className="text-xs font-normal text-text3">
                          {money(depositAmount({ type: 'PERCENT', value: parseDepositPct(state.depositPct) }, preview.totalMinor ?? 0), preview.currency)} when the customer accepts
                        </span>
                      )}
                  </label>
                </>
              ) : (
                <>
                  <label className={labelCls}>
                    Payment terms
                    <select
                      className={`nl-select font-normal ${bad(fields.paymentTerms)}`}
                      value={state.paymentTerms}
                      onChange={(e) => patch({ paymentTerms: e.target.value })}
                    >
                      {TERMS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </label>
                  {state.paymentTerms === 'CUSTOM' && (
                    <label className={labelCls}>
                      Days
                      <input
                        type="number"
                        min={0}
                        max={365}
                        className={`nl-input font-normal ${bad(fields.customTermDays)}`}
                        value={state.customTermDays}
                        onChange={(e) => patch({ customTermDays: e.target.value })}
                      />
                      {fields.customTermDays && <span className={errText}>! {fields.customTermDays}</span>}
                    </label>
                  )}
                  <div className={labelCls}>
                    Due date
                    <span className={`flex h-10 items-center rounded-[10px] bg-surface2 px-3 text-sm font-semibold text-text1 ${fields.paymentTerms ? 'ring-1 ring-neg' : ''}`}>
                      {date(due)} · {days === 0 ? 'on receipt' : `${days} day${days === 1 ? '' : 's'}`}
                    </span>
                    {fields.paymentTerms && <span className={errText}>! {fields.paymentTerms}</span>}
                  </div>
                </>
              )}
              {isTax && (
                <>
                  <label className={labelCls}>
                    Date of supply
                    <input
                      type="date"
                      className={`nl-input font-normal ${bad(fields.supplyDate)}`}
                      value={state.supplyDate}
                      aria-invalid={!!fields.supplyDate || undefined}
                      onChange={(e) => patch({ supplyDate: e.target.value })}
                    />
                    {fields.supplyDate && <span className={errText}>! {fields.supplyDate}</span>}
                  </label>
                  <label className={labelCls}>
                    Buyer TIN{state.customer?.vatRegistered ? ' (required)' : ''}
                    <input
                      className={`nl-input font-mono font-normal ${bad(fields.buyerTin)}`}
                      placeholder="e.g. 114567890-7000"
                      disabled={!state.customer}
                      aria-invalid={!!fields.buyerTin || undefined}
                      value={state.customer?.tin ?? ''}
                      onChange={(e) => setState((s) => (s && s.customer ? { ...s, customer: { ...s.customer, tin: e.target.value } } : s))}
                    />
                    {fields.buyerTin && <span className={errText}>! {fields.buyerTin}</span>}
                  </label>
                </>
              )}
            </section>

            {/* Items */}
            <LineItemsTable
              shopId={shopId}
              lines={state.lines}
              previewLines={preview.lines}
              settings={settings}
              newLineTax={newLineTax}
              errors={fields}
              cardError={fields.lines}
              onChange={(lines) => patch({ lines })}
            />

            {/* Discount, charges and deposit */}
            <section aria-label="Discount, charges and deposit" className={`${cardCls} flex flex-col gap-3 border-borderc`}>
              <h2 className="text-base font-semibold text-text1">Discount, charges and deposit</h2>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3.5">
                <div className={labelCls}>
                  <span>Invoice discount</span>
                  <AmountWithType
                    label="Invoice discount"
                    value={state.invoiceDiscount}
                    type={state.invoiceDiscountType}
                    invalid={!!fields.invoiceDiscount}
                    onValue={(v) => patch({ invoiceDiscount: v })}
                    onType={(t) => patch({ invoiceDiscountType: t })}
                  />
                  {fields.invoiceDiscount && <span className={errText}>! {fields.invoiceDiscount}</span>}
                </div>
                {!isQuote && !isRevision && state.fromQuote && (
                  <label className={labelCls}>
                    Deposit received
                    <input
                      readOnly
                      aria-readonly
                      placeholder="0.00"
                      className="nl-input cursor-default bg-surface2 font-normal tabular-nums"
                      value={state.deposit}
                    />
                    <span className="text-xs font-normal text-text3">
                      Paid on {parentQuote?.number || 'the quotation'}. Applied when issued.
                    </span>
                  </label>
                )}
                {!isQuote && !isRevision && !state.fromQuote && (
                  <label className={labelCls}>
                    Deposit received
                    <input
                      inputMode="decimal"
                      placeholder="0.00"
                      className={`nl-input font-normal tabular-nums ${bad(fields.deposit)}`}
                      value={state.deposit}
                      onChange={(e) => patch({ deposit: e.target.value })}
                    />
                    {fields.deposit && <span className={errText}>! {fields.deposit}</span>}
                  </label>
                )}
              </div>
              {state.charges.map((c, i) => {
                const lErr = fields[chargeField(c.key, 'label')];
                const aErr = fields[chargeField(c.key, 'amount')];
                const set = (p: Partial<typeof c>) => patch({ charges: state.charges.map((x) => (x.key === c.key ? { ...x, ...p } : x)) });
                return (
                  <div key={c.key} className="flex flex-col gap-1">
                    <div className="flex items-center gap-2.5">
                      <input aria-label={`Charge ${i + 1} name`} className={`nl-input min-w-0 flex-1 ${bad(lErr)}`} placeholder="Charge name" value={c.label} onChange={(e) => set({ label: e.target.value })} />
                      <input aria-label={`Charge ${i + 1} amount`} inputMode="decimal" placeholder="0.00" className={`nl-input w-36 text-right tabular-nums ${bad(aErr)}`} value={c.amount} onChange={(e) => set({ amount: e.target.value })} />
                      <button
                        type="button"
                        aria-label={`Remove charge ${i + 1}`}
                        onClick={() => patch({ charges: state.charges.filter((x) => x.key !== c.key) })}
                        className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-[7px] text-lg text-text3 hover:bg-surface2 hover:text-[color:var(--negFg)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
                      >
                        ×
                      </button>
                    </div>
                    {(lErr || aErr) && <span className={errText}>! {lErr || aErr}</span>}
                  </div>
                );
              })}
              <div className="flex flex-wrap gap-2">
                <button type="button" className="nl-btn nl-btn--sm border-dashed border-borderc2 bg-transparent text-text2 hover:text-text1" onClick={() => addCharge('Delivery charge', '')}>
                  + Delivery charge
                </button>
                <button
                  type="button"
                  className="nl-btn nl-btn--sm border-dashed border-borderc2 bg-transparent text-text2 hover:text-text1"
                  onClick={() => addCharge('Service charge 10%', moneyInput(serviceChargeMinor(state, settings)))}
                >
                  + Service charge 10%
                </button>
              </div>
              {state.charges.length > 0 && (
                <span className="text-xs text-text3">
                  {preview.taxLines.length ? 'Charges are taxed at the default rate because items are taxed.' : 'Charges are not taxed while no item is taxed.'}
                </span>
              )}
            </section>

            {/* Notes, terms, attachments */}
            <section aria-label="Notes and terms" className={`${cardCls} flex flex-col gap-3.5 border-borderc`}>
              <label className={labelCls}>
                Notes
                <textarea rows={3} className={`nl-input font-normal ${bad(fields.notes)}`} maxLength={2000} value={state.notes} onChange={(e) => patch({ notes: e.target.value })} />
              </label>
              <label className={labelCls}>
                Terms
                <textarea rows={3} className={`nl-input font-normal ${bad(fields.terms)}`} maxLength={4000} value={state.terms} onChange={(e) => patch({ terms: e.target.value })} />
              </label>
              <div className="flex flex-col gap-1.5">
                <span className="text-[13px] font-semibold text-text2">Attachments</span>
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" disabled className="nl-btn nl-btn--sm nl-btn--secondary border-dashed">+ Attach file</button>
                  <span className="text-xs text-text3">Attachments arrive in a later release.</span>
                </div>
              </div>
              {isRevision && (
                <label className={labelCls}>
                  Reason for the change (optional)
                  <input
                    className={`nl-input font-normal ${bad(fields.revisionNote)}`}
                    maxLength={280}
                    placeholder="Shown in the invoice’s activity"
                    value={state.revisionNote}
                    onChange={(e) => patch({ revisionNote: e.target.value })}
                  />
                </label>
              )}
            </section>

            <TotalsPanel
              doc={shown}
              invoiceDiscountLabel={state.invoiceDiscountType === 'PERCENT' && state.invoiceDiscount.trim() ? ` (${state.invoiceDiscount.trim()}%)` : ''}
            />
          </div>

          {/* Right column: live preview */}
          <section aria-label="Live preview" className="sticky top-0 flex min-w-0 flex-col overflow-hidden rounded-2xl border border-borderc bg-surface">
            <div className="flex items-center justify-between gap-3 border-b border-borderc px-4 py-3">
              <span className="font-semibold text-text1">Live preview</span>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  aria-label="Zoom out"
                  disabled={zoom <= 40}
                  onClick={() => setZoom((z) => Math.max(40, z - 10))}
                  className="nl-btn nl-btn--secondary nl-btn--xs w-7 px-0 text-[15px]"
                >
                  −
                </button>
                <span className="min-w-[44px] text-center text-xs font-semibold tabular-nums text-text1" aria-live="polite">{zoom}%</span>
                <button
                  type="button"
                  aria-label="Zoom in"
                  disabled={zoom >= 140}
                  onClick={() => setZoom((z) => Math.min(140, z + 10))}
                  className="nl-btn nl-btn--secondary nl-btn--xs w-7 px-0 text-[15px]"
                >
                  +
                </button>
              </div>
            </div>
            <div className="max-h-[calc(100vh-200px)] overflow-auto bg-[color:var(--paperCanvas)] p-5">
              <div className="mx-auto" style={{ width: Math.round(595 * zoom / 100) }}>
                <InvoicePreview
                  doc={shown}
                  scale={zoom / 100}
                  shop={shop}
                  branding={{
                    accentColor: settings.branding?.accentColor,
                    footerText: settings.branding?.footerText,
                    bankAccounts: settings.bankAccounts,
                    shopTin: settings.shopTin,
                  }}
                />
              </div>
            </div>
          </section>
        </div>

        {/* Sticky footer */}
        <div className="sticky bottom-0 z-20 -mx-6 -mb-6 flex flex-wrap items-center gap-2.5 border-t border-borderc bg-surface px-6 py-3.5">
          <span className={`min-w-[200px] flex-1 text-[13px] ${errCount ? 'font-semibold text-[color:var(--negFg)]' : 'text-text2'}`}>
            {hint}
            {dirty && !errCount && <span className="ml-2 text-text3">· Unsaved changes</span>}
          </span>
          {isRevision ? (
            <button type="button" className="nl-btn nl-btn--primary" disabled={!!busy || !dirty} onClick={saveDraft}>
              {busy === 'save' ? 'Saving…' : 'Save changes'}
            </button>
          ) : (
            <>
              <button type="button" className="nl-btn nl-btn--secondary" disabled={!!busy} onClick={saveDraft}>
                {busy === 'save' ? 'Saving…' : 'Save draft'}
              </button>
              <button type="button" className="nl-btn nl-btn--soft" disabled={!!busy} onClick={issue}>
                {busy === 'issue' && !sendOpen ? 'Issuing…' : 'Issue'}
              </button>
              <button type="button" className="nl-btn nl-btn--primary" disabled={!!busy} onClick={openIssueSend}>
                Issue and send
              </button>
            </>
          )}
        </div>
      </div>

      {sendOpen && shopId && (
        <IssueSendDialog
          shopId={shopId}
          settings={settings}
          shop={shop}
          state={state}
          preview={preview}
          onClose={() => setSendOpen(false)}
          submit={issueAndSend}
        />
      )}

      {toast && (
        <div role="status" className="fixed bottom-24 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-[10px] bg-[#131318] px-4 py-2.5 text-sm font-medium text-white shadow-lg">
          <span className="text-[color:var(--pos)]">✓</span>
          {toast}
        </div>
      )}
    </div>
  );
}

/**
 * Both routes render this view, so moving between them keeps the component
 * mounted. A fresh editor (new state, new idempotency key) is started on any
 * route change except the one this editor makes itself after its first save.
 */
export default function InvoiceEditorView() {
  const { docId } = useParams();
  const [params] = useSearchParams();
  const created = useRef<string | null>(null);
  const generation = useRef(0);
  const sig = `${docId ?? 'new'}?${params.toString()}`;
  const lastSig = useRef(sig);
  if (sig !== lastSig.current) {
    if (!(docId && docId === created.current)) generation.current += 1;
    lastSig.current = sig;
  }
  return <Editor key={generation.current} onCreated={(id) => { created.current = id; }} />;
}
